import React, { useCallback, useEffect, useState } from 'react'
import { GetAction } from '../../Component/GetAction'
import { PostAction } from '../../Component/PostAction'
import { RequireModule, PageHeader, BTN_PRIMARY } from './shared'

const EMPTY = {
  menu_item_id: '',
  location: 'header',
  label: '',
  url: '',
  sort_order: 0,
  status: 1,
  open_new_tab: 0,
}

function MenuList({
  location,
  title,
  items,
  onEdit,
  onDelete,
  onReorder,
  savingOrder,
}) {
  const [dragId, setDragId] = useState(null)
  const [overId, setOverId] = useState(null)

  const onDragStart = (e, id) => {
    setDragId(id)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(id))
  }

  const onDragOver = (e, id) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (overId !== id) setOverId(id)
  }

  const onDrop = async (e, targetId) => {
    e.preventDefault()
    const sourceId = Number(e.dataTransfer.getData('text/plain') || dragId)
    setDragId(null)
    setOverId(null)
    if (!sourceId || sourceId === targetId) return

    const next = [...items]
    const from = next.findIndex((r) => Number(r.menu_item_id) === sourceId)
    const to = next.findIndex((r) => Number(r.menu_item_id) === targetId)
    if (from < 0 || to < 0) return

    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    await onReorder(location, next)
  }

  const onDragEnd = () => {
    setDragId(null)
    setOverId(null)
  }

  return (
    <div className="manage-card mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="h5 mb-0">{title}</h2>
        {savingOrder ? (
          <span className="text-muted small">Saving order…</span>
        ) : (
          <span className="text-muted small">Drag rows to reorder (autosaves)</span>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-muted mb-0">No items yet. Add a link below.</p>
      ) : (
        <ul className="manage-menu-dnd list-unstyled mb-0">
          {items.map((row) => {
            const id = Number(row.menu_item_id)
            const dragging = dragId === id
            const over = overId === id && dragId !== id
            return (
              <li
                key={id}
                className={`manage-menu-dnd-item${dragging ? ' is-dragging' : ''}${
                  over ? ' is-over' : ''
                }`}
                draggable
                onDragStart={(e) => onDragStart(e, id)}
                onDragOver={(e) => onDragOver(e, id)}
                onDrop={(e) => onDrop(e, id)}
                onDragEnd={onDragEnd}
              >
                <span className="manage-menu-dnd-handle" title="Drag to reorder" aria-hidden>
                  ⋮⋮
                </span>
                <div className="manage-menu-dnd-body">
                  <div className="manage-menu-dnd-label">
                    {row.label}
                    {Number(row.status) !== 1 ? (
                      <span className="manage-menu-badge">Inactive</span>
                    ) : null}
                    {Number(row.open_new_tab) === 1 ? (
                      <span className="manage-menu-badge manage-menu-badge-tab">New tab</span>
                    ) : null}
                  </div>
                  <div className="manage-menu-dnd-url">{row.url}</div>
                </div>
                <div className="manage-menu-dnd-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary mr-2"
                    onClick={() => onEdit(row)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => onDelete(row.menu_item_id)}
                  >
                    Delete
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function MenusPage() {
  const [headerItems, setHeaderItems] = useState([])
  const [footerItems, setFooterItems] = useState([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savingOrder, setSavingOrder] = useState(false)
  const [form, setForm] = useState(EMPTY)

  const load = useCallback(async () => {
    setLoading(true)
    const data = await GetAction('get', { all: 1 }, 'menu/list')
    if (data?.status === 'success' && Array.isArray(data.response)) {
      const rows = data.response
      setHeaderItems(
        rows
          .filter((r) => r.location === 'header')
          .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0))
      )
      setFooterItems(
        rows
          .filter((r) => r.location === 'footer')
          .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0))
      )
      setError('')
    } else {
      setError(data?.message || 'Failed to load menus.')
      setHeaderItems([])
      setFooterItems([])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openNew = (location) => {
    setForm({ ...EMPTY, location })
    setShowForm(true)
    setMessage(null)
  }

  const openEdit = (row) => {
    setForm({
      menu_item_id: row.menu_item_id || '',
      location: row.location || 'header',
      label: row.label || '',
      url: row.url || '',
      sort_order: row.sort_order ?? 0,
      status: Number(row.status) === 1 ? 1 : 0,
      open_new_tab: Number(row.open_new_tab) === 1 ? 1 : 0,
    })
    setShowForm(true)
    setMessage(null)
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!form.label.trim() || !form.url.trim()) {
      setMessage({ error: true, text: 'Label and URL are required.' })
      return
    }
    setSaving(true)
    setMessage(null)
    const payload = {
      menu_item_id: form.menu_item_id || undefined,
      location: form.location,
      label: form.label.trim(),
      url: form.url.trim(),
      sort_order: Number(form.sort_order) || 0,
      status: Number(form.status) === 1 ? 1 : 0,
      open_new_tab: Number(form.open_new_tab) === 1 ? 1 : 0,
    }
    const data = await PostAction('post', payload, 'menu/save')
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Menu item saved.' })
      setShowForm(false)
      await load()
    } else {
      setMessage({
        error: true,
        text: data?.message || 'Failed to save menu item.',
      })
    }
    setSaving(false)
  }

  const onDelete = async (id) => {
    if (!window.confirm('Delete this menu item?')) return
    const data = await PostAction('post', { menu_item_id: id }, 'menu/delete')
    setMessage({
      error: data?.status !== 'success',
      text: data?.message || (data?.status === 'success' ? 'Deleted.' : 'Failed.'),
    })
    if (data?.status === 'success') await load()
  }

  const onReorder = async (location, nextItems) => {
    const ordered = nextItems.map((row, i) => ({
      ...row,
      sort_order: i + 1,
    }))
    if (location === 'header') setHeaderItems(ordered)
    else setFooterItems(ordered)

    setSavingOrder(true)
    const data = await PostAction(
      'post',
      {
        location,
        items: ordered.map((row) => ({
          menu_item_id: row.menu_item_id,
          sort_order: row.sort_order,
        })),
      },
      'menu/reorder'
    )
    setSavingOrder(false)
    if (data?.status === 'success') {
      setMessage({ error: false, text: 'Order saved.' })
    } else {
      setMessage({ error: true, text: data?.message || 'Failed to save order.' })
      await load()
    }
  }

  return (
    <RequireModule moduleKey="menus">
      <PageHeader
        title="Menus"
        subtitle="Configure header and footer navigation links. Drag to change sequence."
        actions={
          <div className="d-flex flex-wrap">
            <button
              type="button"
              className="btn btn-sm btn-primary mr-2"
              style={BTN_PRIMARY}
              onClick={() => openNew('header')}
            >
              Add Header Link
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => openNew('footer')}
            >
              Add Footer Link
            </button>
          </div>
        }
      />

      {message ? (
        <div
          className={`manage-alert ${message.error ? 'manage-alert-err' : 'manage-alert-ok'}`}
        >
          {message.text}
        </div>
      ) : null}
      {error ? <div className="manage-alert manage-alert-err">{error}</div> : null}

      {showForm ? (
        <div className="manage-card mb-4">
          <h2 className="h5 mb-3">
            {form.menu_item_id ? 'Edit Menu Item' : 'Add Menu Item'}
          </h2>
          <form onSubmit={onSubmit}>
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="m_loc">
                  Location
                </label>
                <select
                  id="m_loc"
                  className="form-control"
                  value={form.location}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, location: e.target.value }))
                  }
                >
                  <option value="header">Header</option>
                  <option value="footer">Footer</option>
                </select>
              </div>
              <div className="col-md-5 mb-3">
                <label className="form-label" htmlFor="m_label">
                  Label
                </label>
                <input
                  id="m_label"
                  className="form-control"
                  value={form.label}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, label: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label" htmlFor="m_url">
                  Link / URL
                </label>
                <input
                  id="m_url"
                  className="form-control"
                  placeholder="/Donation or https://…"
                  value={form.url}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, url: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="m_status">
                  Status
                </label>
                <select
                  id="m_status"
                  className="form-control"
                  value={form.status}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, status: Number(e.target.value) }))
                  }
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
              <div className="col-md-3 mb-3 d-flex align-items-end">
                <label className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={Number(form.open_new_tab) === 1}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        open_new_tab: e.target.checked ? 1 : 0,
                      }))
                    }
                  />
                  <span className="form-check-label">Open in new tab</span>
                </label>
              </div>
            </div>
            <div className="d-flex">
              <button
                type="submit"
                className="btn btn-primary mr-2"
                style={BTN_PRIMARY}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowForm(false)}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {loading ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <>
          <MenuList
            location="header"
            title="Header menu"
            items={headerItems}
            onEdit={openEdit}
            onDelete={onDelete}
            onReorder={onReorder}
            savingOrder={savingOrder}
          />
          <MenuList
            location="footer"
            title="Footer menu"
            items={footerItems}
            onEdit={openEdit}
            onDelete={onDelete}
            onReorder={onReorder}
            savingOrder={savingOrder}
          />
        </>
      )}
    </RequireModule>
  )
}

export default MenusPage
